'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { siteConfig } from '@/lib/site-config'

/**
 * §01 Contact button. Opens a small popover with the two ways to reach
 * out: book a meeting directly, or send an email.
 */
export default function ContactPopover() {
  const [open, setOpen] = useState(false)
  const dialogId = useId()
  const rootRef = useRef<HTMLDivElement>(null)

  const close = () => setOpen(false)

  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }
    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative min-h-[48px]">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={dialogId}
        className="btn-secondary min-h-[48px] w-full"
      >
        Contact
      </button>

      {open && (
        <div
          id={dialogId}
          role="dialog"
          aria-label="Contact"
          className="absolute left-0 top-[calc(100%+4px)] z-50 flex w-[260px] flex-col border border-ink bg-paper shadow-lg"
        >
          <a
            href={siteConfig.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className="flex min-h-[48px] items-center border-b border-rule px-4 text-[15px] hover:text-accent"
          >
            Book a meeting
          </a>
          <a
            href={`mailto:${siteConfig.email}`}
            onClick={close}
            className="flex min-h-[48px] items-center px-4 text-[15px] hover:text-accent"
          >
            {siteConfig.email}
          </a>
        </div>
      )}
    </div>
  )
}
